import { Course } from "@/types";
import { useCourses } from "@/hooks/useCourses";
import CourseSearchInline from "./courses/CourseSearchInline";
import AddCourseDialog from "./courses/AddCourseDialog";
import { Loader2, Star } from "lucide-react";

interface CourseSelectorProps {
  onSelectCourse: (course: Course) => void;
}

const CourseSelector = ({ onSelectCourse }: CourseSelectorProps) => {
  const {
    userCourses,
    loading,
    handleToggleFavorite,
    refreshUserCourses,
  } = useCourses();

  return (
    <div className="px-4 pb-20">
      {/* Inline search (local + API import) */}
      <div className="mb-6">
        <CourseSearchInline
          onImported={refreshUserCourses}
          onSelectCourse={onSelectCourse}
        />
      </div>

      {/* My Courses list */}
      <div>
        <h2 className="text-lg font-semibold mb-3">My Courses</h2>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : userCourses.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No courses yet. Search above to import one.
          </p>
        ) : (
          <div className="space-y-1">
            {userCourses.map(course => (
              <button
                key={course.id}
                onClick={() => onSelectCourse(course)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{course.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{course.location}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFavorite(course);
                  }}
                  className="p-1 shrink-0"
                >
                  <Star className={`h-4 w-4 ${course.is_favorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                </button>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Manual add fallback */}
      <div className="mt-6 text-center">
        <AddCourseDialog
          onAddCourse={refreshUserCourses}
          trigger={
            <button className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors">
              Can't find your course? Add it manually
            </button>
          }
        />
      </div>
    </div>
  );
};

export default CourseSelector;
